/**
 * Clinics list page controller — /clinics
 *
 * 純前端列表篩選 + client 分頁（與 articles 同源協作模型）：
 *   - 地區：點 chip 依 data-filter 比對卡片 <li data-region>（"all" 顯示全部）。
 *   - 關鍵字：搜尋框即時比對「診所名 + 地區 + 執業種類 + 主要療程」，清空回到只受地區篩選。
 *   - 篩選命中集合再經 client 分頁（pageSize 9）切片；點頁碼換內容 + ?page=N 同步。
 *
 * 篩選 × 分頁協作（避免兩套系統搶寫 .hidden）：
 *   pagination（bindClientPagination）是「最終可見性」單一擁有者。filter 不直接動 .hidden，
 *   改以 data-eligible 標記每卡是否通過篩選（setVisible override）；篩選變動後 onAfterFilter
 *   呼叫 paginator.refresh() 重算頁數並回第 1 頁。
 *
 * 核心過濾 / chip 委派 / 搜尋 / 空狀態邏輯沿用 list-filter.js（與 articles / products 共用），
 * 本檔注入「如何取地區 / 取搜尋文字」+ 接 pagination。
 *
 * 設計：root 守門早退，找不到 chips / grid 即 return，非該頁不執行、不報錯。
 */

import { createListFilter } from './list-filter.js';
import { bindClientPagination } from '../components/bind-client-pagination.js';

const SELECTORS = {
  chips: '.clinic-filter__chips',
  chip: '.filter-chip',
  grid: '.clinic-grid',
  search: '[data-list-search]',
  empty: '[data-list-empty]',
  pager: '.clinic-pagination',
  clear: '[data-list-clear]', // 7.1 空態「清除篩選」鈕（比照 admin/doctors）
};

const PAGE_SIZE = 9; // 與 clinic-grid 桌機三欄對齊（3×3）。

/** 取卡片可搜尋文字：診所名 + 地區 + 執業種類 + 主要療程，組成單一小寫字串供 includes 比對。 */
function buildSearchText(li) {
  const parts = [
    li.querySelector('.venue-card__name'),
    li.querySelector('.venue-card__location'),
  ].map((el) => (el ? el.textContent : ''));
  // 執業種類 badge + 主要療程 hashtag 一併納入（多節點 → 逐一取文字）
  li.querySelectorAll('.venue-card__tags .badge, .venue-card__hashtags .hashtag')
    .forEach((el) => parts.push(el.textContent));
  // data-region 一併納入（與地區 chip 同源字串），確保打地區名也搜得到
  parts.push(li.getAttribute('data-region') || '');
  return parts.join(' ').toLowerCase();
}

function init() {
  const chipsRoot = document.querySelector(SELECTORS.chips);
  const grid = document.querySelector(SELECTORS.grid);
  if (!chipsRoot || !grid) return null;

  const chips = Array.from(chipsRoot.querySelectorAll(SELECTORS.chip));
  const cards = Array.from(grid.querySelectorAll(':scope > li'));
  if (!chips.length || !cards.length) return null;

  const pagerEl = document.querySelector(SELECTORS.pager);

  // 分頁：合格判定讀 data-eligible（由下方 filter 寫；預設未標記 = 合格，no-JS / 初始全合格）。
  const paginator = pagerEl
    ? bindClientPagination(grid, pagerEl, {
      pageSize: PAGE_SIZE,
      isEligible: (li) => li.dataset.eligible !== 'false',
      // 換頁捲回列表頂，避免長頁換頁後停在頁尾（reduced-motion 由瀏覽器決定 smooth/auto）。
      onPageChange: () => grid.scrollIntoView({ behavior: 'smooth', block: 'start' }),
    })
    : null;

  const filter = createListFilter({
    chipsRoot,
    chips,
    searchInput: document.querySelector(SELECTORS.search),
    cards,
    emptyEl: document.querySelector(SELECTORS.empty),
    getCategory: (li) => li.getAttribute('data-region') || '',
    getSearchText: buildSearchText,
    // pagination 擁有 .hidden：filter 只標記合格性（data-eligible），不直接顯隱卡片。
    setVisible: paginator
      ? (card, visible) => { card.dataset.eligible = visible ? 'true' : 'false'; }
      : undefined,
    // 篩選變動 → 分頁重算回第 1 頁；無 pager 時 createListFilter 走預設 .hidden 行為（零回歸）。
    onAfterFilter: paginator ? () => paginator.refresh() : undefined,
  });

  // 7.1 空態「清除篩選」：清關鍵字 + 切回「全部」地區 chip（比照 admin-doctor-filter.js 單一事實源——
  // 不另寫重置邏輯，改清空搜尋框後派 input 事件、再點「全部」chip 走既有委派路徑）。
  const clearBtn = document.querySelector(SELECTORS.clear);
  const searchInput = document.querySelector(SELECTORS.search);
  const allChip = chips.find((c) => (c.getAttribute('data-filter') || 'all') === 'all');
  let onClear = null;
  if (clearBtn && allChip) {
    onClear = () => {
      if (searchInput) {
        searchInput.value = '';
        searchInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
      allChip.click();
    };
    clearBtn.addEventListener('click', onClear);
    window.addEventListener('pagehide', () => clearBtn.removeEventListener('click', onClear), { once: true });
  }

  return { filter, paginator };
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
